import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { 
  Loader2, Users, Building2, Sparkles, AlertTriangle, CheckCircle, 
  RefreshCw, Database, Shield, ListChecks, Settings2, BarChart3,
  GitMerge, XCircle, Eye, User, Wrench, ChevronDown, ChevronRight,
  TrendingUp, TrendingDown, Save, Target, Lightbulb, Clock, Calendar, FolderTree
} from 'lucide-react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { format, subDays, startOfDay } from "date-fns";
import { findCandidateDuplicates, findCompanyDuplicates } from '@/lib/deduplication-utils';
import { CandidateDeduplicationPanel } from '@/components/data-quality/CandidateDeduplicationPanel';
import { CompanyDeduplicationPanel } from '@/components/data-quality/CompanyDeduplicationPanel';
import { SkillDeduplicationPanel } from '@/components/data-quality/SkillDeduplicationPanel';
import { BucketDeduplicationPanel } from '@/components/data-quality/BucketDeduplicationPanel';

interface DataQualityMetrics {
  totalCandidates: number;
  candidatesWithEmail: number;
  candidatesWithPhone: number;
  candidatesWithResume: number;
  totalCompanies: number;
  companiesWithWebsite: number;
  companiesWithLocation: number;
  totalSkills: number;
  duplicateCandidateGroups: number;
  duplicateCompanyGroups: number;
}

interface ReviewItem {
  id: string;
  entity_type: string;
  entity_a_id: string;
  entity_b_id: string;
  entity_a_data: any;
  entity_b_data: any;
  match_score: number;
  rule_breakdown: Record<string, number>;
  status: string;
  priority: number;
  created_at: string;
}

interface MatchingRule {
  id: string;
  entity_type: string;
  rule_name: string;
  description: string | null;
  field_config: any[];
  base_weight: number;
  learned_weight: number;
  auto_merge_threshold: number;
  review_threshold: number;
  is_active: boolean;
  success_count: number;
  rejection_count: number;
}

const COLORS = ["hsl(var(--primary))", "hsl(var(--destructive))", "hsl(var(--muted))"];

// Bulk Scan Component
const BulkScanSection = () => {
  const [scanning, setScanning] = useState<Record<string, boolean>>({});
  const [results, setResults] = useState<Record<string, any>>({});
  const queryClient = useQueryClient();

  const entityTypes = [
    { key: 'skill', label: 'Skills', icon: Sparkles, color: 'text-violet-500' },
    { key: 'company', label: 'Companies', icon: Building2, color: 'text-emerald-500' },
    { key: 'candidate', label: 'Candidates', icon: Users, color: 'text-blue-500' },
    { key: 'bucket', label: 'Buckets', icon: FolderTree, color: 'text-purple-500' },
  ];

  const runBulkScan = async (entityType: string) => {
    setScanning(prev => ({ ...prev, [entityType]: true }));
    setResults(prev => ({ ...prev, [entityType]: null }));

    try {
      const { data, error } = await supabase.functions.invoke('cleanup-duplicates', {
        body: { entity_type: entityType, batch_size: 200, dry_run: false }
      });

      if (error) throw error;

      setResults(prev => ({ ...prev, [entityType]: data }));
      queryClient.invalidateQueries({ queryKey: ["dedup-review-queue"] });
      queryClient.invalidateQueries({ queryKey: ["dedup-review-counts"] });

      if (data.queued_for_review > 0 || data.auto_merged > 0) {
        toast.success(`${entityType}: ${data.auto_merged} auto-merged, ${data.queued_for_review} queued for review`);
      } else {
        toast.info(`${entityType}: No duplicates found in ${data.processed} records`);
      }
    } catch (err: any) {
      console.error(`Error scanning ${entityType}:`, err);
      toast.error(`Failed to scan ${entityType}: ${err.message}`);
      setResults(prev => ({ ...prev, [entityType]: { error: err.message } }));
    } finally {
      setScanning(prev => ({ ...prev, [entityType]: false }));
    }
  };

  const runAllScans = async () => {
    for (const type of entityTypes) {
      await runBulkScan(type.key);
    }
  };

  const isAnyScanning = Object.values(scanning).some(Boolean);

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-primary/20 p-2">
              <Target className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Bulk Duplicate Scan</CardTitle>
              <CardDescription>
                Run deduplication rules against existing data to find and merge duplicates
              </CardDescription>
            </div>
          </div>
          <Button 
            onClick={runAllScans} 
            disabled={isAnyScanning}
            className="gap-2"
          >
            {isAnyScanning ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Scan All
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {entityTypes.map(({ key, label, icon: Icon, color }) => {
            const result = results[key];
            const isScanning = scanning[key];

            return (
              <Card key={key} className="overflow-hidden">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className={`h-5 w-5 ${color}`} />
                      <span className="font-medium">{label}</span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => runBulkScan(key)}
                      disabled={isScanning}
                    >
                      {isScanning ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  
                  {result && !result.error && (
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div>
                        <p className="text-lg font-bold text-muted-foreground">{result.processed}</p>
                        <p className="text-muted-foreground">Scanned</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-green-600">{result.auto_merged}</p>
                        <p className="text-muted-foreground">Merged</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-amber-600">{result.queued_for_review}</p>
                        <p className="text-muted-foreground">Queued</p>
                      </div>
                    </div>
                  )}
                  
                  {result?.error && (
                    <p className="text-xs text-destructive">{result.error}</p>
                  )}
                  
                  {!result && !isScanning && (
                    <p className="text-xs text-muted-foreground text-center">
                      Click to scan for duplicates
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const DataQuality = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  const queryClient = useQueryClient();

  // Overview state
  const [metrics, setMetrics] = useState<DataQualityMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);

  // Review Queue state
  const [selectedEntityType, setSelectedEntityType] = useState<string>("all");
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [comparisonItem, setComparisonItem] = useState<ReviewItem | null>(null);

  // Rule Engine state
  const [ruleEntityType, setRuleEntityType] = useState<string>("skill");
  const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());
  const [editedRules, setEditedRules] = useState<Record<string, Partial<MatchingRule>>>({});

  // Analytics state
  const [timeRange, setTimeRange] = useState<string>("7d");

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  // ========== OVERVIEW DATA ==========
  useEffect(() => {
    if (activeTab === 'overview') {
      fetchMetrics();
    }
  }, [activeTab]);

  const fetchMetrics = async () => {
    setMetricsLoading(true);
    try {
      const [candidatesRes, companiesRes, skillsRes, resumeRes, pendingRes] = await Promise.all([
        supabase.from('candidates').select('id, first_name, last_name, email_primary, phone_primary'),
        supabase.from('companies').select('id, name, website_url, location'),
        supabase.from('skills').select('id, name'),
        supabase.from('candidate_resumes').select('candidate_id', { count: 'exact', head: true }),
        supabase.from('dedup_review_queue').select('entity_type').eq('status', 'pending'),
      ]);

      const candidates = candidatesRes.data || [];
      const companies = companiesRes.data || [];
      const skills = skillsRes.data || [];
      const pendingQueue = pendingRes.data || [];

      const candidatePending = pendingQueue.filter(s => s.entity_type === 'candidate').length;
      const companyPending = pendingQueue.filter(s => s.entity_type === 'company').length;
      const candidateDuplicates = findCandidateDuplicates(candidates, 80);
      const companyDuplicates = findCompanyDuplicates(companies);

      setMetrics({
        totalCandidates: candidates.length,
        candidatesWithEmail: candidates.filter(c => c.email_primary).length,
        candidatesWithPhone: candidates.filter(c => c.phone_primary).length,
        candidatesWithResume: resumeRes.count || 0,
        totalCompanies: companies.length,
        companiesWithWebsite: companies.filter(c => c.website_url).length,
        companiesWithLocation: companies.filter(c => c.location).length,
        totalSkills: skills.length,
        duplicateCandidateGroups: Math.max(candidateDuplicates.length, candidatePending),
        duplicateCompanyGroups: Math.max(companyDuplicates.length, companyPending),
      });
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setMetricsLoading(false);
    }
  };

  // ========== REVIEW QUEUE DATA ==========
  const { data: reviewItems, isLoading: reviewLoading } = useQuery({
    queryKey: ["dedup-review-queue", selectedEntityType],
    queryFn: async () => {
      let query = supabase
        .from("dedup_review_queue")
        .select("*")
        .eq("status", "pending")
        .order("priority", { ascending: false })
        .order("created_at", { ascending: true });
      
      if (selectedEntityType !== "all") {
        query = query.eq("entity_type", selectedEntityType);
      }
      
      const { data, error } = await query.limit(100);
      if (error) throw error;
      return data as ReviewItem[];
    },
    enabled: activeTab === 'queue'
  });

  const { data: queueCounts } = useQuery({
    queryKey: ["dedup-review-counts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("dedup_review_queue")
        .select("entity_type, status");
      if (error) throw error;
      
      const counts: Record<string, { pending: number; reviewed: number }> = {
        skill: { pending: 0, reviewed: 0 },
        company: { pending: 0, reviewed: 0 },
        candidate: { pending: 0, reviewed: 0 }
      };
      
      for (const item of data || []) {
        if (counts[item.entity_type]) {
          if (item.status === "pending") counts[item.entity_type].pending++;
          else counts[item.entity_type].reviewed++;
        }
      }
      
      return counts;
    }
  });

  const processMutation = useMutation({
    mutationFn: async ({ itemId, decision }: { itemId: string; decision: "merged" | "rejected" | "deferred" }) => {
      const item = reviewItems?.find(i => i.id === itemId);
      if (!item) throw new Error("Item not found");

      await supabase.functions.invoke("zingg-match-service", {
        body: {
          action: "record_feedback",
          entityType: item.entity_type,
          entityAId: item.entity_a_id,
          entityBId: item.entity_b_id,
          matchScore: item.match_score,
          ruleScores: item.rule_breakdown,
          decision,
          entity: item.entity_a_data
        }
      });

      if (decision === "merged") {
        await performMerge(item);
      }

      const { error } = await supabase
        .from("dedup_review_queue")
        .update({ 
          status: decision === "deferred" ? "pending" : "reviewed",
          reviewed_at: new Date().toISOString()
        })
        .eq("id", itemId);

      if (error) throw error;
    },
    onSuccess: (_, { decision }) => {
      queryClient.invalidateQueries({ queryKey: ["dedup-review-queue"] });
      queryClient.invalidateQueries({ queryKey: ["dedup-review-counts"] });
      toast.success(`Item ${decision === "merged" ? "merged" : decision === "rejected" ? "rejected" : "deferred"} successfully`);
    },
    onError: (error) => {
      toast.error("Failed to process item: " + error.message);
    }
  });

  const performMerge = async (item: ReviewItem) => {
    const { entity_type, entity_a_id, entity_b_id } = item;

    switch (entity_type) {
      case "skill":
        await supabase.from("candidate_skills").update({ skill_id: entity_a_id }).eq("skill_id", entity_b_id);
        await supabase.from("job_skills").update({ skill_id: entity_a_id }).eq("skill_id", entity_b_id);
        await supabase.from("skills").delete().eq("id", entity_b_id);
        await supabase.from("skill_merge_events").insert({
          primary_skill_id: entity_a_id,
          merged_skill_id: entity_b_id,
          merged_skill_name: item.entity_b_data?.name || "Unknown"
        });
        break;
      case "company":
        await supabase.from("candidate_experience").update({ company_id: entity_a_id }).eq("company_id", entity_b_id);
        await supabase.from("companies").update({ canonical_id: entity_a_id }).eq("id", entity_b_id);
        await supabase.from("company_dedupe_events").insert({
          primary_company_id: entity_a_id,
          merged_company_id: entity_b_id
        });
        break;
      case "candidate":
        await supabase.from("candidate_applications").update({ candidate_id: entity_a_id }).eq("candidate_id", entity_b_id);
        await supabase.from("candidates").update({ canonical_id: entity_a_id }).eq("id", entity_b_id);
        await supabase.from("candidate_dedupe_events").insert({
          primary_candidate_id: entity_a_id,
          merged_candidate_id: entity_b_id
        });
        break;
    }
  };

  // ========== RULE ENGINE DATA ==========
  const { data: rules, isLoading: rulesLoading } = useQuery({
    queryKey: ["matching-rules"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("matching_rules")
        .select("*")
        .order("entity_type", { ascending: true })
        .order("base_weight", { ascending: false });
      if (error) throw error;
      return data as MatchingRule[];
    },
    enabled: activeTab === 'rules'
  });

  const updateRuleMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<MatchingRule> }) => {
      const { error } = await supabase.from("matching_rules").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["matching-rules"] });
      toast.success("Rule updated successfully");
    },
    onError: (error) => {
      toast.error("Failed to update rule: " + error.message);
    }
  });

  // ========== ANALYTICS DATA ==========
  const { data: feedbackData, isLoading: analyticsLoading } = useQuery({
    queryKey: ["match-feedback-analytics", timeRange],
    queryFn: async () => {
      const days = timeRange === "7d" ? 7 : timeRange === "30d" ? 30 : 90;
      const startDate = startOfDay(subDays(new Date(), days));
      
      const { data, error } = await supabase
        .from("matching_feedback")
        .select("*")
        .gte("created_at", startDate.toISOString())
        .order("created_at", { ascending: true });
      
      if (error) throw error;
      return data;
    },
    enabled: activeTab === 'analytics'
  });

  // Helper functions
  const getEntityIcon = (type: string) => {
    switch (type) {
      case "skill": return <Wrench className="h-4 w-4" />;
      case "company": return <Building2 className="h-4 w-4" />;
      case "candidate": return <User className="h-4 w-4" />;
      default: return null;
    }
  };

  const getEntityLabel = (data: any, type: string) => {
    switch (type) {
      case "skill": return data?.name || "Unknown Skill";
      case "company": return data?.name || "Unknown Company";
      case "candidate": return `${data?.first_name || ""} ${data?.last_name || ""}`.trim() || "Unknown Candidate";
      default: return "Unknown";
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 0.9) return "text-green-600";
    if (score >= 0.8) return "text-yellow-600";
    return "text-orange-600";
  };

  const totalPending = Object.values(queueCounts || {}).reduce((sum, c) => sum + c.pending, 0);
  const totalDuplicates = (metrics?.duplicateCandidateGroups || 0) + (metrics?.duplicateCompanyGroups || 0);

  // Analytics calculations
  const totalFeedback = feedbackData?.length || 0;
  const merges = feedbackData?.filter(f => f.user_decision === "merged").length || 0;
  const rejections = feedbackData?.filter(f => f.user_decision === "rejected").length || 0;
  const deferred = feedbackData?.filter(f => f.user_decision === "deferred").length || 0;
  const mergeRate = totalFeedback > 0 ? Math.round((merges / totalFeedback) * 100) : 0;

  const feedbackByDay = () => {
    if (!feedbackData) return [];
    const grouped: Record<string, { merged: number; rejected: number; deferred: number }> = {};
    for (const item of feedbackData) {
      const day = format(new Date(item.created_at), "MMM dd");
      if (!grouped[day]) grouped[day] = { merged: 0, rejected: 0, deferred: 0 };
      grouped[day][item.user_decision as keyof typeof grouped[string]]++;
    }
    return Object.entries(grouped).map(([date, values]) => ({ date, ...values }));
  };

  const decisionPieData = [
    { name: "Merged", value: merges },
    { name: "Rejected", value: rejections },
    { name: "Deferred", value: deferred }
  ].filter(d => d.value > 0);

  const getTabLabel = () => {
    switch (activeTab) {
      case 'queue': return 'Review Queue';
      case 'rules': return 'Rule Engine';
      case 'analytics': return 'Analytics';
      case 'candidates': return 'Candidates';
      case 'companies': return 'Companies';
      case 'skills': return 'Skills';
      case 'buckets': return 'Buckets';
      default: return 'Overview';
    }
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/admin">Admin</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            {activeTab === 'overview' ? (
              <BreadcrumbPage>Data Quality</BreadcrumbPage>
            ) : (
              <BreadcrumbLink href="/admin/data-quality" onClick={(e) => { e.preventDefault(); handleTabChange('overview'); }}>
                Data Quality
              </BreadcrumbLink>
            )}
          </BreadcrumbItem>
          {activeTab !== 'overview' && (
            <>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{getTabLabel()}</BreadcrumbPage>
              </BreadcrumbItem>
            </>
          )}
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-6 w-6" />
            Data Quality Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Monitor, deduplicate, and manage data quality with AI-powered matching
          </p>
        </div>
        <div className="flex items-center gap-2">
          {totalPending > 0 && (
            <Badge variant="destructive" className="px-3 py-1">
              {totalPending} pending review
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={() => queryClient.invalidateQueries()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="grid w-full grid-cols-8 lg:w-auto lg:inline-grid">
          <TabsTrigger value="overview" className="gap-2">
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="queue" className="gap-2">
            <ListChecks className="h-4 w-4" />
            <span className="hidden sm:inline">Queue</span>
            {totalPending > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 px-1.5 text-xs">{totalPending}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="rules" className="gap-2">
            <Settings2 className="h-4 w-4" />
            <span className="hidden sm:inline">Rules</span>
          </TabsTrigger>
          <TabsTrigger value="analytics" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden sm:inline">Analytics</span>
          </TabsTrigger>
          <TabsTrigger value="candidates" className="gap-2">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Candidates</span>
          </TabsTrigger>
          <TabsTrigger value="companies" className="gap-2">
            <Building2 className="h-4 w-4" />
            <span className="hidden sm:inline">Companies</span>
          </TabsTrigger>
          <TabsTrigger value="skills" className="gap-2">
            <Sparkles className="h-4 w-4" />
            <span className="hidden sm:inline">Skills</span>
          </TabsTrigger>
          <TabsTrigger value="buckets" className="gap-2">
            <FolderTree className="h-4 w-4" />
            <span className="hidden sm:inline">Buckets</span>
          </TabsTrigger>
        </TabsList>

        {/* ========== OVERVIEW TAB ========== */}
        <TabsContent value="overview" className="space-y-6">
          {metricsLoading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Candidates</p>
                        <p className="text-2xl font-bold">{metrics?.totalCandidates || 0}</p>
                      </div>
                      <Users className="h-8 w-8 text-blue-500/60" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Companies</p>
                        <p className="text-2xl font-bold">{metrics?.totalCompanies || 0}</p>
                      </div>
                      <Building2 className="h-8 w-8 text-emerald-500/60" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Pending Review</p>
                        <p className="text-2xl font-bold">{totalPending}</p>
                      </div>
                      <AlertTriangle className="h-8 w-8 text-amber-500/60" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-violet-500/10 to-violet-600/5 border-violet-500/20">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Skills</p>
                        <p className="text-2xl font-bold">{metrics?.totalSkills || 0}</p>
                      </div>
                      <Sparkles className="h-8 w-8 text-violet-500/60" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Action Required */}
              {totalPending > 0 && (
                <Card className="border-amber-500/50 bg-gradient-to-r from-amber-500/10 to-orange-500/5">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-amber-600">
                      <AlertTriangle className="h-5 w-5" />
                      Action Required: Items Pending Review
                    </CardTitle>
                    <CardDescription>
                      AI has detected potential duplicates that need your review
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button onClick={() => handleTabChange('queue')} className="gap-2">
                      <ListChecks className="h-4 w-4" />
                      Review Queue ({totalPending} items)
                    </Button>
                  </CardContent>
                </Card>
              )}

              {totalPending === 0 && (
                <Card className="border-emerald-500/50 bg-gradient-to-r from-emerald-500/10 to-green-500/5">
                  <CardContent className="flex items-center gap-4 p-6">
                    <div className="rounded-full bg-emerald-500/20 p-3">
                      <CheckCircle className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-emerald-600">All Clear!</h3>
                      <p className="text-sm text-muted-foreground">No pending items in the review queue</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Quick Actions */}
              <div className="grid gap-4 md:grid-cols-3">
                <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => handleTabChange('rules')}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <Settings2 className="h-8 w-8 text-primary" />
                      <div>
                        <h3 className="font-semibold">Configure Rules</h3>
                        <p className="text-sm text-muted-foreground">Tune matching thresholds and weights</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => handleTabChange('analytics')}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <BarChart3 className="h-8 w-8 text-primary" />
                      <div>
                        <h3 className="font-semibold">View Analytics</h3>
                        <p className="text-sm text-muted-foreground">Monitor deduplication performance</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => handleTabChange('candidates')}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <Users className="h-8 w-8 text-primary" />
                      <div>
                        <h3 className="font-semibold">Manual Review</h3>
                        <p className="text-sm text-muted-foreground">Manually review and merge duplicates</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* ========== REVIEW QUEUE TAB ========== */}
        <TabsContent value="queue" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(["skill", "company", "candidate"] as const).map(type => (
              <Card key={type} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setSelectedEntityType(type)}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getEntityIcon(type)}
                      <div>
                        <p className="font-medium capitalize">{type}s</p>
                        <p className="text-2xl font-bold">{queueCounts?.[type]?.pending || 0}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Reviewed</p>
                      <p className="font-medium">{queueCounts?.[type]?.reviewed || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <Select value={selectedEntityType} onValueChange={setSelectedEntityType}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="skill">Skills</SelectItem>
                <SelectItem value="company">Companies</SelectItem>
                <SelectItem value="candidate">Candidates</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Card>
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                {reviewLoading ? (
                  <div className="flex justify-center py-20">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : reviewItems?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
                    <p className="text-lg font-medium">Queue is empty!</p>
                    <p className="text-muted-foreground">No items pending review</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {reviewItems?.map(item => (
                      <div key={item.id} className="p-4 hover:bg-muted/50 transition-colors">
                        <div className="flex items-start gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              {getEntityIcon(item.entity_type)}
                              <Badge variant="outline" className="capitalize">{item.entity_type}</Badge>
                              <span className={`font-bold ${getScoreColor(item.match_score)}`}>
                                {Math.round(item.match_score * 100)}% match
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="p-3 bg-muted rounded-lg">
                                <p className="text-xs text-muted-foreground mb-1">Primary</p>
                                <p className="font-medium truncate">{getEntityLabel(item.entity_a_data, item.entity_type)}</p>
                              </div>
                              <div className="p-3 bg-muted rounded-lg">
                                <p className="text-xs text-muted-foreground mb-1">Duplicate</p>
                                <p className="font-medium truncate">{getEntityLabel(item.entity_b_data, item.entity_type)}</p>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button size="sm" onClick={() => processMutation.mutate({ itemId: item.id, decision: "merged" })} disabled={processMutation.isPending}>
                              <GitMerge className="h-4 w-4 mr-1" />
                              Merge
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => processMutation.mutate({ itemId: item.id, decision: "rejected" })} disabled={processMutation.isPending}>
                              <XCircle className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setComparisonItem(item)}>
                              <Eye className="h-4 w-4 mr-1" />
                              Compare
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Comparison Dialog */}
          <Dialog open={!!comparisonItem} onOpenChange={() => setComparisonItem(null)}>
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle>Compare Entities</DialogTitle>
                <DialogDescription>Side-by-side comparison of the potential duplicate</DialogDescription>
              </DialogHeader>
              {comparisonItem && (
                <div className="grid grid-cols-2 gap-6">
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Badge>Primary</Badge>
                      {getEntityLabel(comparisonItem.entity_a_data, comparisonItem.entity_type)}
                    </h4>
                    <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-[300px]">
                      {JSON.stringify(comparisonItem.entity_a_data, null, 2)}
                    </pre>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Badge variant="secondary">Duplicate</Badge>
                      {getEntityLabel(comparisonItem.entity_b_data, comparisonItem.entity_type)}
                    </h4>
                    <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-[300px]">
                      {JSON.stringify(comparisonItem.entity_b_data, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ========== RULES TAB ========== */}
        <TabsContent value="rules" className="space-y-6">
          {/* Bulk Scan Section */}
          <BulkScanSection />

          <Tabs value={ruleEntityType} onValueChange={setRuleEntityType}>
            <TabsList>
              <TabsTrigger value="skill">Skills</TabsTrigger>
              <TabsTrigger value="company">Companies</TabsTrigger>
              <TabsTrigger value="candidate">Candidates</TabsTrigger>
              <TabsTrigger value="bucket">Buckets</TabsTrigger>
            </TabsList>

            <TabsContent value={ruleEntityType} className="mt-6">
              {rulesLoading ? (
                <div className="flex justify-center py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-4">
                  {rules?.filter(r => r.entity_type === ruleEntityType).map(rule => {
                    const isExpanded = expandedRules.has(rule.id);
                    const hasChanges = !!editedRules[rule.id];
                    const performanceScore = rule.success_count + rule.rejection_count > 0 
                      ? Math.round((rule.success_count / (rule.success_count + rule.rejection_count)) * 100) 
                      : null;

                    return (
                      <Card key={rule.id} className={!rule.is_active ? "opacity-60" : ""}>
                        <Collapsible open={isExpanded} onOpenChange={() => {
                          setExpandedRules(prev => {
                            const next = new Set(prev);
                            if (next.has(rule.id)) next.delete(rule.id);
                            else next.add(rule.id);
                            return next;
                          });
                        }}>
                          <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <CollapsibleTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                  </Button>
                                </CollapsibleTrigger>
                                <div>
                                  <CardTitle className="text-lg">{rule.rule_name.replace(/_/g, " ")}</CardTitle>
                                  <CardDescription>{rule.description}</CardDescription>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                {performanceScore !== null && (
                                  <span className={`font-medium ${performanceScore >= 70 ? "text-green-600" : "text-yellow-600"}`}>
                                    {performanceScore}%
                                  </span>
                                )}
                                <Badge variant={rule.is_active ? "default" : "secondary"}>
                                  {rule.is_active ? "Active" : "Inactive"}
                                </Badge>
                                <Switch
                                  checked={rule.is_active}
                                  onCheckedChange={() => updateRuleMutation.mutate({ id: rule.id, updates: { is_active: !rule.is_active } })}
                                />
                              </div>
                            </div>
                          </CardHeader>
                          <CollapsibleContent>
                            <CardContent className="pt-4 space-y-6">
                              <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-3">
                                  <Label>Auto-Merge Threshold</Label>
                                  <div className="flex items-center gap-4">
                                    <Slider
                                      value={[Number(editedRules[rule.id]?.auto_merge_threshold ?? rule.auto_merge_threshold) * 100]}
                                      onValueChange={([v]) => setEditedRules(prev => ({ ...prev, [rule.id]: { ...prev[rule.id], auto_merge_threshold: v / 100 } }))}
                                      max={100}
                                      min={50}
                                      step={1}
                                      className="flex-1"
                                    />
                                    <span className="w-16 text-right font-mono">
                                      {Math.round(Number(editedRules[rule.id]?.auto_merge_threshold ?? rule.auto_merge_threshold) * 100)}%
                                    </span>
                                  </div>
                                </div>
                                <div className="space-y-3">
                                  <Label>Review Threshold</Label>
                                  <div className="flex items-center gap-4">
                                    <Slider
                                      value={[Number(editedRules[rule.id]?.review_threshold ?? rule.review_threshold) * 100]}
                                      onValueChange={([v]) => setEditedRules(prev => ({ ...prev, [rule.id]: { ...prev[rule.id], review_threshold: v / 100 } }))}
                                      max={100}
                                      min={30}
                                      step={1}
                                      className="flex-1"
                                    />
                                    <span className="w-16 text-right font-mono">
                                      {Math.round(Number(editedRules[rule.id]?.review_threshold ?? rule.review_threshold) * 100)}%
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                                <div className="text-center">
                                  <p className="text-2xl font-bold text-green-600">{rule.success_count}</p>
                                  <p className="text-xs text-muted-foreground">Successful Merges</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-2xl font-bold text-red-600">{rule.rejection_count}</p>
                                  <p className="text-xs text-muted-foreground">Rejections</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-2xl font-bold">{rule.success_count + rule.rejection_count}</p>
                                  <p className="text-xs text-muted-foreground">Total Decisions</p>
                                </div>
                              </div>
                              {hasChanges && (
                                <div className="flex justify-end">
                                  <Button onClick={() => {
                                    updateRuleMutation.mutate({ id: rule.id, updates: editedRules[rule.id] });
                                    setEditedRules(prev => { const next = { ...prev }; delete next[rule.id]; return next; });
                                  }}>
                                    <Save className="h-4 w-4 mr-2" />
                                    Save Changes
                                  </Button>
                                </div>
                              )}
                            </CardContent>
                          </CollapsibleContent>
                        </Collapsible>
                      </Card>
                    );
                  })}
                  {rules?.filter(r => r.entity_type === ruleEntityType).length === 0 && (
                    <Card>
                      <CardContent className="flex flex-col items-center justify-center py-12">
                        <Settings2 className="h-12 w-12 text-muted-foreground mb-4" />
                        <p className="text-lg font-medium">No rules configured</p>
                        <p className="text-muted-foreground">Add matching rules for this entity type</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* ========== ANALYTICS TAB ========== */}
        <TabsContent value="analytics" className="space-y-6">
          <div className="flex justify-end">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-[150px]">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Decisions</p>
                    <p className="text-3xl font-bold">{totalFeedback}</p>
                  </div>
                  <Target className="h-8 w-8 text-primary" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Merge Rate</p>
                    <p className="text-3xl font-bold text-green-600">{mergeRate}%</p>
                  </div>
                  <GitMerge className="h-8 w-8 text-green-600" />
                </div>
                <Progress value={mergeRate} className="mt-2" />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Merges</p>
                    <p className="text-3xl font-bold">{merges}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Rejections</p>
                    <p className="text-3xl font-bold">{rejections}</p>
                  </div>
                  <XCircle className="h-8 w-8 text-destructive" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Decisions Over Time</CardTitle>
                <CardDescription>Daily breakdown of merge decisions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {analyticsLoading ? (
                    <div className="flex justify-center items-center h-full">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={feedbackByDay()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="merged" stroke="hsl(var(--primary))" strokeWidth={2} />
                        <Line type="monotone" dataKey="rejected" stroke="hsl(var(--destructive))" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Decision Distribution</CardTitle>
                <CardDescription>Overall breakdown of decisions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {analyticsLoading ? (
                    <div className="flex justify-center items-center h-full">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  ) : decisionPieData.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full">
                      <p className="text-muted-foreground">No data available</p>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={decisionPieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {decisionPieData.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ========== MANUAL DEDUP TABS ========== */}
        <TabsContent value="candidates" className="m-0">
          <CandidateDeduplicationPanel onMergeComplete={fetchMetrics} />
        </TabsContent>

        <TabsContent value="companies" className="m-0">
          <CompanyDeduplicationPanel onMergeComplete={fetchMetrics} />
        </TabsContent>

        <TabsContent value="skills" className="m-0">
          <SkillDeduplicationPanel />
        </TabsContent>

        <TabsContent value="buckets" className="m-0">
          <BucketDeduplicationPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DataQuality;
